<template>
    <div id="graph">
        <canvas id="projectedGraph" width="600" height="300"></canvas>
        <div v-if="projectionData" class="projection-summary">
            <div class="summary-item">
                <span class="label">Market Value</span>
                <span class="value">${{ formatNumber(projectionData.currentValue) }}</span>
            </div>
            <div class="summary-item">
                <span class="label">Invested</span>
                <span class="value">${{ formatNumber(projectionData.netInvested) }}</span>
            </div>
            <div class="summary-item">
                <span class="label">P&L</span>
                <span class="value" :class="projectionData.unrealisedPnL >= 0 ? 'positive' : 'negative'">
                    ${{ formatNumber(projectionData.unrealisedPnL) }}
                </span>
            </div>
            <div class="summary-item">
                <span class="label">Monthly</span>
                <span class="value">${{ formatNumber(projectionData.monthlyContribution) }}</span>
            </div>
            <div class="summary-item">
                <span class="label">Yield</span>
                <span class="value">{{ projectionData.dividendYieldPercent }}%</span>
            </div>
        </div>

    </div>
</template>

<script>
    import axios from 'axios';
    import Chart from 'chart.js/auto';

    export default {
        props: {
            portfolioguid: String,
            marketid: Number
        },
        data: function () {
            return {
                portfolioGuid: this.portfolioguid,
                marketId: this.marketid,
                projectionData: null,
                chart: null
            };
        },
        methods: {
            formatNumber(value) {
                if (Math.abs(value) >= 1000) {
                    return (value / 1000).toFixed(1) + 'k';
                }
                return value.toLocaleString();
            },
            async getProjectionData() {
                const { data } = await axios.get('/api/trades/GetProjection', {
                    params: {
                        portfolioGuid: this.portfolioGuid,
                        marketId: this.marketId
                    }
                });

                this.projectionData = data;

                // Add Year 0 (current) to the beginning
                const labels = ['Now', ...data.projections.map(p => p.label)];
                const projectedValues = [data.currentValue, ...data.projections.map(p => p.projectedValue)];
                const totalContributions = [data.currentValue, ...data.projections.map(p => p.totalContributions)];
                // dividendPerYear contains projected dividends expected to be received in that year
                const dividendPerYear = [0, ...data.projections.map(p => p.dividendGrowth)];

                var chartConfig = {
                    type: 'bar',
                    data: {
                        labels: labels,
                        datasets: [
                            {
                                label: 'Total Contributions',
                                data: totalContributions,
                                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                                borderColor: 'rgba(54, 162, 235, 1)',
                                borderWidth: 1,
                                stack: 'stack1'
                            },
                            {
                                // Show projected dividends per year stacked on top of contributions (same left axis)
                                label: 'Projected Dividends / Year',
                                data: dividendPerYear,
                                backgroundColor: 'rgba(75, 192, 92, 0.7)',
                                borderColor: 'rgba(75, 192, 92, 1)',
                                borderWidth: 1,
                                stack: 'stack1'
                            },
                            {
                                // Keep projected value as a line on the right axis so it doesn't participate in stacking
                                label: 'Projected Value',
                                data: projectedValues,
                                type: 'line',
                                borderColor: 'rgba(255, 99, 132, 1)',
                                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                                borderWidth: 2,
                                fill: false,
                                tension: 0.3,
                                pointRadius: 4,
                                yAxisID: 'y1'
                            }
                        ]
                    },
                    options: {
                        responsive: true,
                        plugins: {
                            title: {
                                display: true,
                                text: 'Portfolio Projection (VGS, VHY, VAS)'
                            },
                            tooltip: {
                                callbacks: {
                                    label: function (context) {
                                        return context.dataset.label + ': $' + context.raw.toLocaleString();
                                    }
                                }
                            }
                        },
                        scales: {
                            x: {
                                stacked: true
                            },
                            // Left axis for contributions and dividends (stacked)
                            y: {
                                stacked: true,
                                ticks: {
                                    callback: function (value) {
                                        return '$' + value.toLocaleString();
                                    }
                                }
                            },
                            // Right axis for the projected value line
                            y1: {
                                position: 'right',
                                grid: {
                                    drawOnChartArea: false
                                },
                                ticks: {
                                    callback: function (value) {
                                        return '$' + value.toLocaleString();
                                    }
                                }
                            }
                        }
                    }
                };

                // Destroy existing chart if it exists
                if (this.chart) {
                    this.chart.destroy();
                }

                const ctx = document.getElementById('projectedGraph').getContext('2d');
                this.chart = new Chart(ctx, chartConfig);
            }
        },
        async mounted() {
            await this.getProjectionData();
        },
        beforeUnmount() {
            if (this.chart) {
                this.chart.destroy();
            }
        }
    }
</script>

<style scoped>
    .projection-summary {
        display: flex;
        gap: 15px;
        margin-bottom: 8px;
        padding: 8px 12px;
        background-color: #f8f9fa;
        border-radius: 4px;
        border-left: 3px solid #36a2eb;
        font-size: 12px;
    }

    .summary-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

        .summary-item .label {
            color: #6c757d;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .summary-item .value {
            font-weight: 600;
            color: #333;
        }

            .summary-item .value.positive {
                color: #28a745;
            }

            .summary-item .value.negative {
                color: #dc3545;
            }

    .holdings-summary {
        display: flex;
        gap: 8px;
        margin-bottom: 10px;
        flex-wrap: wrap;
    }

    .holding-badge {
        background-color: #e9ecef;
        padding: 3px 8px;
        border-radius: 3px;
        font-size: 11px;
        color: #495057;
    }
</style>
